import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';

import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import StudentGroupDelete from './StudentGroupDelete';
import CustomIcon from '../../common/CustomIcon';
import ParticipantCard from './ParticipantCard';
import StudentGroupContainer from './StudentGroupContainer';

import * as classroomGroupActions from '../../../actions/classroom/classroomGroupActions';
import { PARTICIPANTS_TABS_CONSTANTS, ROLE } from '../../../const';
import ParticipantCardsPlaceholder from './ParticipantCardsPlaceholder';
import SadMonster from '../../common/EmptyListPlaceholder/SadMonster';

import { CardMosaic, AddGroup } from './ParticipantsComponents';
import { Selector } from '@netexknowledge/smart-components';

export class StudentList extends React.Component {
  addGroup = () => {
    const { isAppOnline } = this.props;
    if (isAppOnline) {
      const { classroomGroupActions } = this.props;
      classroomGroupActions.setAddGroupIsOpen(true);
    }
  };

  renderAddGroup = () => (
    <AddGroup onClick={this.addGroup} data-cy="addGroupButton">
      <CustomIcon name="plus-button" size={24} />
    </AddGroup>
  );

  renderParticipantList = participants => {
    const { group, isTeacherView } = this.props;

    if (!participants || participants.length === 0) {
      return this.renderEmptyClassPlaceholder();
    }

    const studentCards = participants.map((participant, index) => (
      <ParticipantCard key={index} student={participant} group={group} isTeacherView={isTeacherView} />
    ));

    return (
      <CSSTransitionGroup
        transitionName="init"
        transitionAppear={true}
        transitionAppearTimeout={900}
        transitionEnter={false}
        transitionLeave={false}
      >
        {studentCards}
      </CSSTransitionGroup>
    );
  };

  renderGroupsColumn = () => (
    <div className="column groups-column groups-column-color">
      {!this.props.isTeacherView && (
        <div key="groups" className="groups">
          <h5>{I18n.t('studentList.groups')}</h5>
          {this.isTeacher() && this.renderAddGroup()}
          <StudentGroupContainer />
        </div>
      )}
    </div>
  );

  renderEmptyClassPlaceholder = () => {
    return (
      <CSSTransitionGroup
        transitionName="init"
        transitionAppear={true}
        transitionAppearTimeout={500}
        transitionEnter={false}
        transitionLeave={false}
      >
        <div className="centered-container">
          <SadMonster msgKey="studentList.emptyPlaceholderMessage" />
        </div>
      </CSSTransitionGroup>
    );
  };

  isTeacher = () => this.props.role === ROLE.INSTRUCTOR;

  renderParticipants = () => {
    const { groupId, students, teachers, groupStudents, isTeacherView } = this.props;
    return groupId !== 0
      ? this.renderParticipantList(groupStudents)
      : isTeacherView
      ? this.renderParticipantList(teachers)
      : this.renderParticipantList(students);
  };

  getSelectOptions = () => {
    const mapped = Object.keys(PARTICIPANTS_TABS_CONSTANTS)
      .map(key => {
        if (PARTICIPANTS_TABS_CONSTANTS.hasOwnProperty(key)) {
          const option = { value: PARTICIPANTS_TABS_CONSTANTS[key].key };
          option.name = I18n.t(PARTICIPANTS_TABS_CONSTANTS[key].label);
          return option;
        }
      })
      .map(item => ({ [item.value]: item.name }));
    return Object.assign({}, ...mapped);
  };

  handleClickMenuItem = option => {
    if (this.props.onSelectorChange) {
      this.props.onSelectorChange(option);
    }
  };

  render() {
    const { groupId, classes, fetchingParticipants, isTeacherView } = this.props;

    return (
      <div className={`participants-list-view ${classes ? classes : ''}`}>
        <div style={{ width: '100%' }}>
          {groupId === 0 && (
            <div className="dropdown-container selector-component">
              <Selector
                id="user-type-filter"
                elements={this.getSelectOptions()}
                onChange={e =>
                  this.handleClickMenuItem({ value: e, name: I18n.t(PARTICIPANTS_TABS_CONSTANTS[e].label) })
                }
                selectedKey={this.props.selectMenuOption.key}
              />
            </div>
          )}
          <div className="student-list-container">
            <div className="student-list-content">
              <CardMosaic>
                {fetchingParticipants ? <ParticipantCardsPlaceholder /> : this.renderParticipants()}
              </CardMosaic>
            </div>
          </div>
        </div>
        {groupId === 0 && !isTeacherView && this.renderGroupsColumn()}
      </div>
    );
  }
}

StudentList.propTypes = {
  groupStudents: PropTypes.array,
  students: PropTypes.array,
  role: PropTypes.string,
  classroomGroupActions: PropTypes.object,
  group: PropTypes.object,
  isAppOnline: PropTypes.bool,
  groups: PropTypes.array,
  idClassroom: PropTypes.string,
  teachers: PropTypes.array,
  activeClassroomTab: PropTypes.string,
  groupId: PropTypes.any,
  classes: PropTypes.string,
  fetchingParticipants: PropTypes.bool,
  onSelectorChange: PropTypes.func,
  onToggleMenu: PropTypes.func,
  onCloseMenu: PropTypes.func,
  selectMenuOption: PropTypes.object,
  open: PropTypes.bool
};

const generateGroupStudents = (groupId, groups, students) => {
  if (groupId === 0) {
    return [];
  }

  const selectedGroup = groups.filter(group => group.id === groupId);
  return students.filter(student => selectedGroup[0].participants.includes(student._id));
};

function mapStateToProps(state) {
  return {
    students: state.classrooms.selectedClassroom.participants.filter(participant => participant.role === ROLE.STUDENT),
    teachers: state.classrooms.selectedClassroom.participants.filter(
      participant => participant.role === ROLE.INSTRUCTOR || participant.role === ROLE.COINSTRUCTOR
    ),
    groups: state.classrooms.selectedClassroom.groups,
    listState: state.classrooms.selectedClassroom.listState,
    classroom: state.classrooms.selectedClassroom.classroom,
    fetchingParticipants: state.classrooms.selectedClassroom.fetchingParticipants,
    group: state.selectedGroup.group,
    i18n: state.i18n,
    isAppOnline: state.connection.status.online,
    idClassroom: state.router.params.idClassroom,
    task: state.task.selectedTask,
    activeClassroomTab: state.classrooms.selectedClassroom.activeClassroomTab,
    groupId: state.selectedGroup.group.id,
    groupStudents: generateGroupStudents(
      state.selectedGroup.group.id,
      state.classrooms.selectedClassroom.groups,
      state.classrooms.selectedClassroom.participants.filter(participant => participant.role === ROLE.STUDENT)
    ),
    role: state.classrooms.selectedClassroom.classroom.role
  };
}

function mapDispatchToProps(dispatch) {
  return {
    classroomGroupActions: bindActionCreators(classroomGroupActions, dispatch),
    dispatch
  };
}

const DragDropContextStudentList = DragDropContext(HTML5Backend)(StudentList);

export default connect(mapStateToProps, mapDispatchToProps)(DragDropContextStudentList);
